/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { CurrencyType } from '../Localization/Currency';
import LocalizationComponent from '../Localization/LocalizationComponent';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ComponentBase from '../Utility/ComponentBase';
import { CurrencyUtil } from '../Utility/CurrencyUtil';
import ClassUtil from '../Utility/ClassUtil';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

export class RecallEntryData
{
    public index: number;
    public time: string;
    public betAmount: number;
    public winAmount: number;
    public endingBalance: number;
    public currencyType: CurrencyType;
    public onClick: () => void;

    constructor(
        index: number,
        time: string,
        betAmount: number,
        winAmount: number,
        endingBalance: number,
        currencyType: CurrencyType,
        onClick: () => void)
    {
        this.index = index;
        this.time = time;
        this.betAmount = betAmount;
        this.winAmount = winAmount;
        this.endingBalance = endingBalance;
        this.currencyType = currencyType;
        this.onClick = onClick;
    }
}

/** Reference class for the various UI components of a recall entry list item. */
@cc._decorator.ccclass
export default class RecallEntry extends ComponentBase
{
    /** Label for the play number of this recall entry. */
    @property({ type: cc.Label, visible: true })
    private _dateTime: cc.Label = null;

    /** Label for the starting credits of this recall entry. */
    @property({ type: cc.Label, visible: true })
    private _betAmount: cc.Label = null;

    /** Label for the win amount of this recall entry. */
    @property({ type: cc.Label, visible: true })
    private _winAmount: cc.Label = null;

    /** Label for the ending credits of this recall entry. */
    @property({ type: cc.Label, visible: true })
    private _endingBalance: cc.Label = null;

    private _localRef: InjectableComponent<LocalizationComponent>
        = new InjectableComponent<LocalizationComponent>();

    private static readonly _currencyOptions: object = { maximumFractionDigits: 0 };

    private _onClick: () => void = null;

    protected setContexts(): void
    {
        this._localRef.SetContext(LocalizationComponent);
    }

    protected onLoad(): void
    {
        super.onLoad();

        const clickHandler: cc.Component.EventHandler = new cc.Component.EventHandler();
        clickHandler.target = this.node;
        clickHandler.component = ClassUtil.getName(RecallEntry);
        clickHandler.handler = "onClick";

        const button: cc.Button = this.getComponentInChildren(cc.Button);
        button.clickEvents.push(clickHandler);
    }

    protected onClick = (): void =>
    {
        this._onClick();
    }

    /**
     * Initialization function to set the value for the relevant UI components all at once.
     * @param playNumber This recall entry's play number.
     * @param startingCredits This recall entry's starting credits amount.
     * @param endingCredits This recall entry's ending credits amount.
     * @param winAmount This recall entry's win amount.
     */
    public Initialize(
        index: number,
        time: string,
        betAmount: number,
        winAmount: number,
        endingBalance: number,
        currencyType: CurrencyType,
        onClick: () => void): void
    {
        if (!this.isContextSet) { this.setContexts(); }

        this._onClick = onClick;
        this._dateTime.string = time;

        const isCash: boolean = currencyType === CurrencyType.CASH;
        this._betAmount.string = this.getCurrencyDisplay(betAmount, isCash);
        this._winAmount.string = this.getCurrencyDisplay(winAmount, isCash);
        this._endingBalance.string = this.getCurrencyDisplay(endingBalance, isCash);
    }

    private getCurrencyDisplay(amount: number, isCash: boolean): string
    {
        return isCash
            ? CurrencyUtil.GetCurrencyString(this._localRef.Value, amount)
            : amount.toLocaleString(this._localRef.Value.CultureKey, RecallEntry._currencyOptions);
    }

    public InitializeWithData(data: RecallEntryData): void
    {
        this.Initialize(
            data.index,
            data.time,
            data.betAmount,
            data.winAmount,
            data.endingBalance,
            data.currencyType,
            data.onClick
        );
    }
}
